import { isValidEmail } from '@domain-group/javascript-utils'

export type AgencyModel = {
  active: boolean,
  address_line_1: string,
  address_line_2?: string,
  address_postcode: string,
  address_region: string,
  address_state: string,
  assigned_brand_group_ids?: number[],
  assigned_provider_ids?: number[],
  bank_account_bsb?: string,
  bank_account_name?: string,
  bank_account_number?: string,
  billing_type_id?: number,
  brand_group_id?: number,
  brand_id: number,
  brand_theme_id?: number,
  business_name?: string,
  business_number?: string,
  cache_key: number,
  campaign_fee?: number,
  campaign_fee_cap?: number,
  campaign_management_platform?: '' | 'campaigntrack' | 'other' | 'none',
  cover_letter?: string,
  data?: {
    external_ids?: {
      realbase?: number,
    },
    support_team?: {
      user_ids?: number[],
    },
  },
  default_image?: string,
  email: string,
  fax?: string,
  features?: {
    alpha?: boolean,
    business_process_management?: boolean,
    campaign_management?: boolean,
    digital_signing?: boolean,
    external_ticketing?: boolean,
    marketing_dashboard?: boolean,
    new_order_approval?: boolean,
    presentation_mode?: boolean,
    secure_mode?: boolean,
  },
  geo_country_id?: number,
  id: number,
  invoice_pdf_file?: number,
  license_number?: string,
  locked_by_user_id: number,
  options?: {
    accounting_platform_api?: string,
    crm_sync_api?: string,
    data_provider_api?: 'core_logic' | 'pricefinder',
    default_xero_account?: string,
    default_xero_credit_account?: string,
    disabled_integration_modules: [string],
    disabled_portal_option_values?: string,
    electronic_signature_integrable?: string,
    electronic_signature_provider?: string,
    enabled_portal_option_values?: string,
    engage_platform?: 'legacy' | 'standalone',
    export_settings?: {
      height?: number,
      size?: string,
      width?: number,
    },
    hidden_product_ids?: number[],
    include_campaign_fee_on_quotes?: boolean,
    new_quote_default?: string,
    payment_terms_unit?: string,
    payment_terms_value?: string,
    provider_account_id?: number,
    publication_planner?: 'V1' | 'V2',
    quote_payment_template_id?: number,
    quote_template_id?: number,
    recalculate_quote_editions?: boolean,
    recalculate_quote_template_prices?: boolean,
    require_artwork_data_sync?: boolean,
    require_quote_authorization?: boolean,
    require_quote_contributions?: boolean,
    require_quote_for_orders?: boolean,
    require_quote_item_required_by_date?: boolean,
    skip_watermark_plans?: boolean,
    sync_xero_payments?: string,
    system_branding_key?: '' | 'campaigntrack' | 'newsextend',
    task_prices?: object,
    ticketing_system?: string,
    watermark_images?: any[],
    watermark_settings?: {
      opacity?: number,
      padding?: {
        horizontal?: number,
        vertical?: number,
      },
      position?: string,
    },
    workflow_statuses?: any[],
    xero_integration?: 'V1' | 'V2',
  },
  phone: string,
  pricing_plan_id: number,
  principal_email: string,
  principal_mobile: string,
  principal_name: string,
  provider_metrics?: {
    metrics: {
      orders: {
        count: {
          total: number,
          ytd: number,
        },
      },
      value: {
        total: number,
        ytd: number,
        monthly: Array<{ label: string, value: number }>,
      },
      services: {
        breakdowns: Array<{ label: string, value: number }>,
      },
      invoices: {
        outstanding: {
          total: number,
        },
      },
    },
  },
  provider_account_id?: number,
  realhub_platform?: 'legacy' | 'standalone_v1',
  quote_pdf_file?: {
    original?: string,
  },
  salesforce_id?: string,
  salesforce_details?: {
    active: boolean,
    address_country: string,
    address_line_1: string,
    address_postcode: string,
    address_region: string,
    address_state: string,
    brand_id: number,
    business_name: string,
    business_number: string,
    email: string,
    geo_country_id: number,
    license_number: string,
    phone: string,
    principal_email: string,
    principal_mobile: string,
    principal_name: string,
    title: string,
    website: string,
  },
  shipping_address?: string,
  short_name?: string,
  subscription_fee?: number,
  subscription_start_date?: string,
  subscription_user_limit?: number,
   quote_template_display_data?: {
    agent_display_name?: string,
    campaign_display_name?: string,
    quote_template_display_name?: string,
    vendor_display_name?: string,
  },
  title: string,
  type: string,
  website: string,
  zuora_account_number?: string,
  zuora_charge_number?: string,
  zuora_id?: string,
  zuora_subscription_number?: string,
}

export type AgencyRequestOptions = {}

const state = {
  active: true,
  address_line_1: '',
  address_line_2: '',
  address_postcode: '',
  address_region: '',
  address_state: '',
  bank_account_bsb: '',
  bank_account_name: '',
  bank_account_number: '',
  billing_type_id: undefined,
  brand_id: undefined,
  brand_theme_id: undefined,
  business_name: '',
  business_number: '',
  campaign_fee_cap: undefined,
  campaign_fee: undefined,
  cover_letter: '',
  data: {},
  email: '',
  fax: '',
  features: {},
  geo_country_id: undefined,
  id: null,
  invoice_pdf_file: null,
  license_number: '',
  locked_by_user_id: undefined,
  options: {
    accounting_platform_api: '',
    crm_sync_api: '',
    default_xero_account: '',
    default_xero_credit_account: '',
    disabled_integration_modules: [],
    electronic_signature_integrable: 'company',
    electronic_signature_provider: '',
    export_settings: {
      height: 1200,
      size: 'original',
      width: 1600,
    },
    include_campaign_fee_on_quotes: true,
    new_quote_default: 'blank',
    payment_terms_unit: 'months',
    payment_terms_value: '',
    quote_payment_template_id: undefined,
    quote_template_id: undefined,
    recalculate_quote_editions: false,
    recalculate_quote_template_prices: true,
    require_artwork_data_sync: true,
    require_quote_authorization: false,
    require_quote_contributions: false,
    require_quote_for_orders: false,
    require_quote_item_required_by_date: false,
    skip_watermark_plans: false,
    sync_xero_payments: '',
    task_prices: {},
    ticketing_system: '',
    watermark_images: [],
    watermark_settings: {
      opacity: 0.7,
      padding: {
        horizontal: 20,
        vertical: 20,
      },
      position: 'south-east',
    },
    workflow_statuses: [],
  },
  phone: '',
  pricing_plan_id: undefined,
  principal_email: '',
  principal_mobile: '',
  principal_name: '',
  quote_pdf_file: null,
  salesforce_id: '',
  short_name: '',
  subscription_fee: null,
  subscription_start_date: '',
  subscription_user_limit: null,
  title: '',
  website: '',
  zuora_account_number: '',

}

export const requiredFields = [
  { key: 'address_line_1' },
  { key: 'address_postcode' },
  { key: 'address_region' },
  { key: 'address_state' },
  { key: 'brand_id' },
  { key: 'email', validation: isValidEmail, invalidMessage: 'Invalid Email format' },
  { key: 'geo_country_id' },
  { key: 'phone' },
  { key: 'title' },
  { key: 'website' },
]

export default state
